import { MenuOutlined, SearchOutlined } from '@ant-design/icons';
import { useOidc } from '@axa-fr/react-oidc';
import { clearAllUserSession, IconButton, twClassMerge } from '@healthhub/react-ui';
import { Divider, Typography } from 'antd';
import { useEffect } from 'react';

import { PortalOutlinedCircleCloseIcon, PortalOutlinedMailIcon, PortalOutlinedUserIcon } from '@shared/assets-icon';
import { SmartLink } from '@shared/components';
import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import useIsAuthenticated from '@shared/hooks/useIsAuthenticated';
import useToggle from '@shared/hooks/useToggle/useToggle';
import { persistor } from '@shared/stores';
import { clearCookies } from '@shared/utils/cookie';

import { GlobalSearchInput } from './GlobalSearchInput';
import InboxNotification from './InboxNotification';
import { NewsLetterModal } from './NewsLetterModal';
import { useHeaderMenu } from '../hooks';

const { Text } = Typography;

interface HeaderActionProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  toggleMenu: () => void;
}

const HeaderAction = ({ isMenuOpen, setIsMenuOpen, toggleMenu }: HeaderActionProps) => {
  const { login, logout } = useOidc();

  const { isAuthenticated } = useIsAuthenticated();
  const { subHeaderMenu } = useHeaderMenu();
  const [isGlobalSearchOpen, toggleGlobalSearch, setIsGlobalSearchOpen] = useToggle(false);

  const [isNewsLetterModalOpen, toggleNewsLetterModalOpen] = useToggle(false);

  const onClickLogin = async () => {
    login();
  };

  const onClickLogout = async () => {
    clearAllUserSession({ persistor, clearCookies });
    await logout(sitecoreHomeBaseUrl());
  };

  const onMenuClick = () => {
    toggleMenu();
    setIsGlobalSearchOpen(false);
  };

  const onGlobalSearchClick = () => {
    toggleGlobalSearch();
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const buttonProps = isAuthenticated
    ? {
        onClick: onClickLogout,
        Icon: PortalOutlinedUserIcon,
        label: 'Log out',
      }
    : {
        onClick: onClickLogin,
        Icon: PortalOutlinedUserIcon,
        label: 'Log in',
      };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [isMenuOpen]);

  return (
    <div className="flex flex-col justify-center gap-4">
      <div className="flex items-center gap-4">
        <GlobalSearchInput isOpen={isGlobalSearchOpen} />
        <Divider className="m-0 hidden h-8 border-dashed border-secondary-grey-steel lg:block" type="vertical" />
        <div className="hidden items-center gap-4 lg:flex">
          <button
            className="flex items-center text-xs font-medium text-primary-green-4 lg:text-lg"
            lang="en"
            onClick={toggleNewsLetterModalOpen}
          >
            <PortalOutlinedMailIcon className="size-6 shrink-0" />
            Sign up now
          </button>
          <NewsLetterModal isOpen={isNewsLetterModalOpen} onClose={toggleNewsLetterModalOpen} />
          <Divider className="m-0 hidden h-8 border-dashed border-secondary-grey-steel lg:block" type="vertical" />
        </div>
        <InboxNotification className="hidden lg:flex" />
        <Divider className="m-0 hidden h-8 border-dashed border-secondary-grey-steel lg:block" type="vertical" />
        <div className="flex items-center">
          <div className="flex items-center gap-1">
            <button
              className="flex w-min items-center gap-1 text-xs font-medium text-primary-green-4 sm:w-auto md:text-nowrap lg:text-lg"
              lang="en"
              onClick={buttonProps.onClick}
            >
              <buttonProps.Icon className="size-6" />
              {buttonProps.label}
            </button>
          </div>
          <Divider className="ml-4 mr-0 h-8 border-dashed border-secondary-grey-steel lg:hidden" type="vertical" />
          <div className="flex items-center lg:hidden">
            <IconButton
              aria-label={isGlobalSearchOpen ? 'Close global search' : 'Open global search'}
              className="json-checked flex size-14 items-center justify-center"
              icon={
                isGlobalSearchOpen ? (
                  <PortalOutlinedCircleCloseIcon className="h-4.5 w-4.5 " />
                ) : (
                  <SearchOutlined className="text-xl text-primary-blue-grey-3" />
                )
              }
              onClick={onGlobalSearchClick}
            />
            <Divider className="m-0 h-8 border-dashed border-secondary-grey-steel lg:hidden" type="vertical" />
            <InboxNotification className="flex lg:hidden" />
            <IconButton
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              className={twClassMerge(
                'json-checked h-14 w-14 items-center bg-secondary-green-100 justify-center flex',
                isMenuOpen && 'bg-primary-green-4'
              )}
              icon={
                isMenuOpen ? (
                  <PortalOutlinedCircleCloseIcon className="h-4.5 w-4.5 [&>path]:fill-white" />
                ) : (
                  <MenuOutlined className="text-xl" />
                )
              }
              onClick={onMenuClick}
            />
          </div>
        </div>
      </div>
      <div className="hidden gap-6 self-end lg:flex">
        {subHeaderMenu
          .filter((item) => item.isPublic !== isAuthenticated || item.isPublic)
          .map(({ label, url }) => (
            <SmartLink anchorProps={{ target: '_blank' }} key={label} to={url}>
              <Text className="text-base font-normal text-primary-blue-grey-3" key={label} lang="en">
                {label}
              </Text>
            </SmartLink>
          ))}
      </div>
    </div>
  );
};

export default HeaderAction;
